import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const API = API_BASE_URL;

/**
 * DisqualifiedTab — edge-to-edge table of disqualified candidates.
 * Search, sort by last stage/reason/date, view details, or reactivate.
 */
const DisqualifiedTab = ({ pipelineId, onCardClick, onReload }) => {
  const [disqualified, setDisqualified] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [reactivating, setReactivating] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const token = localStorage.getItem('authToken');
  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadDisqualified(); }, [pipelineId]);

  const loadDisqualified = async () => {
    try {
      const res = await axios.get(`${API}/api/pipelines/${pipelineId}/cards?status=disqualified`, { headers: authHeaders });
      setDisqualified(res.data || []);
    } catch (err) { console.error('Error loading disqualified:', err); }
    finally { setLoading(false); }
  };

  const handleReactivate = async (e, cardId, name) => {
    e.stopPropagation();
    if (!window.confirm(`Reactivate ${name}?\n\nThis will move them back to the client interview stages.`)) return;
    setReactivating(cardId);
    try {
      await axios.put(`${API}/api/client/cards/${cardId}/reactivate`, {}, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      await loadDisqualified();
      onReload?.();
    } catch (err) { console.error('Error reactivating:', err); alert('Failed to reactivate'); }
    finally { setReactivating(null); }
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const filtered = disqualified.filter(c =>
    (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.current_title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.current_company || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sorted = useMemo(() => {
    if (!sortField) return filtered;
    return [...filtered].sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      if (sortField === 'stage') {
        const av = a.rejected_at_stage_name || a.stage_name || '';
        const bv = b.rejected_at_stage_name || b.stage_name || '';
        return av.localeCompare(bv) * dir;
      }
      if (sortField === 'reason') {
        const av = a.disqualification_reason || '';
        const bv = b.disqualification_reason || '';
        return av.localeCompare(bv) * dir;
      }
      if (sortField === 'date') {
        const av = a.updated_at ? new Date(a.updated_at).getTime() : 0;
        const bv = b.updated_at ? new Date(b.updated_at).getTime() : 0;
        return (av - bv) * dir;
      }
      return 0;
    });
  }, [filtered, sortField, sortDir]);

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  const SortHeader = ({ field, children }) => (
    <th onClick={() => toggleSort(field)}
      className="px-4 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none">
      <div className="flex items-center gap-0.5">
        {children}
        <svg className={`w-3 h-3 flex-shrink-0 ${sortField === field ? 'text-blue-600' : 'text-gray-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {sortField === field && sortDir === 'desc'
            ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />}
        </svg>
      </div>
    </th>
  );

  if (loading) return <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600"></div></div>;

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="px-4 py-2 flex items-center gap-3 border-b bg-white flex-shrink-0">
        <div className="relative flex-1 max-w-xs">
          <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search disqualified..." className="w-full pl-7 pr-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500" />
          <svg className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <span className="text-[11px] text-gray-400 tabular-nums"><span className="font-medium text-gray-500">{sorted.length}</span> of {disqualified.length} disqualified</span>
      </div>

      {/* Table */}
      {sorted.length === 0 ? (
        <div className="text-center py-16">
          <svg className="w-10 h-10 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-gray-400">{searchTerm ? 'No matches' : 'No disqualified candidates'}</p>
        </div>
      ) : (
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b sticky top-0 z-10">
              <tr>
                <th className="px-4 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Candidate</th>
                <SortHeader field="stage">Last Stage</SortHeader>
                <SortHeader field="reason">Reason</SortHeader>
                <SortHeader field="date">Date</SortHeader>
                <th className="px-4 py-2 text-right text-[10px] font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sorted.map(card => (
                <tr key={card.id} onClick={() => onCardClick?.(card)} className="hover:bg-red-50/50 cursor-pointer transition-colors">
                  <td className="px-4 py-2.5">
                    <p className="text-xs font-medium text-gray-900">{card.name}</p>
                    <p className="text-[10px] text-gray-500">{card.current_title}{card.current_company ? ` · ${card.current_company}` : ''}</p>
                  </td>
                  <td className="px-4 py-2.5">
                    {card.status === 'withdrew' ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-purple-100 text-purple-700">Withdrew{card.rejected_at_stage_name ? ` at ${card.rejected_at_stage_name}` : ''}</span>
                    ) : card.rejected_at_stage_name ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-orange-100 text-orange-700">Rejected at {card.rejected_at_stage_name}</span>
                    ) : (
                      <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-gray-100 text-gray-600">{card.stage_name || 'Initial Review'}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="text-xs text-gray-500 line-clamp-1">{card.disqualification_reason || '—'}</span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">{fmtDate(card.updated_at)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={(e) => handleReactivate(e, card.id, card.name)} disabled={reactivating === card.id}
                      className="text-[10px] px-2 py-1 text-green-700 bg-green-50 border border-green-200 rounded hover:bg-green-100 disabled:opacity-50">
                      {reactivating === card.id ? 'Moving...' : 'Reactivate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DisqualifiedTab;
